import React, { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Badge,
  Button,
  CheckCircleIcon,
  CogIcon,
  DocumentTextIcon,
  EyeIcon,
  LockIcon,
  PlusIcon,
  SaveIcon,
  Select,
  TableIcon,
  TrashIcon,
  ViewColumnsIcon,
  useConfirm,
} from '../../components/ui';
import { ChartTrendingUpIcon, ChevronLeftIcon, ChevronRightIcon } from '../../components/ui/Icons';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import {
  deleteEFormAppPage,
  publishEFormDefinition,
  unpublishEFormDefinition,
} from '../../services/mutations';
import BuilderPage from './BuilderPage';
import {
  FormStudioActionsProvider,
  type FormStudioActions,
} from './FormStudioContext';
import PlatformSettingsPage from './PlatformSettingsPage';
import {
  flattenSchemaFields,
  isEFormDisplayField,
  validateSchemaForPublish,
} from './schema';
import {
  getDraftVersion,
  getPublishedVersion,
  useEFormAppPages,
  useEFormDefinition,
  useEFormPrintLayout,
  useEFormRelations,
  useEFormSavedReports,
} from './useData';
import { asPrintDocument } from './printLayoutModel';
import { EFormAppPageEditor } from './AppPages';
import ReportsPage from './ReportsPage';

type StudioSection =
  | 'overview'
  | 'design'
  | 'access'
  | 'field-access'
  | 'workflow'
  | 'relations'
  | 'reports'
  | 'pages'
  | 'print'
  | 'publish';

const SECTIONS: Array<{
  id: StudioSection;
  label: string;
  group: 'Start' | 'Build' | 'Configure' | 'Compose' | 'Release';
  icon: React.ReactNode;
  hint: string;
}> = [
  { id: 'overview', label: 'Overview', group: 'Start', icon: <CogIcon className="h-4 w-4" />, hint: 'Setup progress and status' },
  { id: 'design', label: 'Form design', group: 'Build', icon: <DocumentTextIcon className="h-4 w-4" />, hint: 'Fields, sections and list columns' },
  { id: 'access', label: 'Access roles', group: 'Configure', icon: <LockIcon className="h-4 w-4" />, hint: 'Who can use this form' },
  { id: 'field-access', label: 'Field permissions', group: 'Configure', icon: <EyeIcon className="h-4 w-4" />, hint: 'Hide or lock sensitive fields' },
  { id: 'workflow', label: 'Workflow', group: 'Configure', icon: <CheckCircleIcon className="h-4 w-4" />, hint: 'Approvals and outcome actions' },
  { id: 'relations', label: 'Data & relations', group: 'Configure', icon: <TableIcon className="h-4 w-4" />, hint: 'Connect this form to others' },
  { id: 'reports', label: 'Reports', group: 'Compose', icon: <ChartTrendingUpIcon className="h-4 w-4" />, hint: 'Saved pivots and schedules' },
  { id: 'pages', label: 'Dashboard widgets', group: 'Compose', icon: <ViewColumnsIcon className="h-4 w-4" />, hint: 'Tiles for the central dashboard' },
  { id: 'print', label: 'Print', group: 'Compose', icon: <DocumentTextIcon className="h-4 w-4" />, hint: 'Template or designed PDF' },
  { id: 'publish', label: 'Publish', group: 'Release', icon: <CheckCircleIcon className="h-4 w-4" />, hint: 'Review and make changes live' },
];

function normalizeSection(value: string | null): StudioSection {
  return SECTIONS.some((section) => section.id === value) ? (value as StudioSection) : 'overview';
}

const StudioSectionIntro: React.FC<{
  title: string;
  description: string;
  status?: string;
  children: React.ReactNode;
}> = ({ title, description, status, children }) => (
  <div className="space-y-4">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <div className="flex items-center gap-2">
          <h2 className="text-xl font-semibold tracking-tight">{title}</h2>
          {status ? <Badge variant="secondary">{status}</Badge> : null}
        </div>
        <p className="mt-1 max-w-3xl text-sm text-slate-500 dark:text-slate-400">{description}</p>
      </div>
    </div>
    {children}
  </div>
);

const STUDIO_NAV_COLLAPSED_KEY = 'eform-studio-nav-collapsed';

const FormStudioPage: React.FC = () => {
  const definitionId = Number(useParams<{ id: string }>().id);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const section = normalizeSection(searchParams.get('section'));
  const definition = useEFormDefinition(Number.isInteger(definitionId) ? definitionId : 0);
  const { colors } = useTheme();
  const [actions, setActions] = useState<FormStudioActions | null>(null);
  const [navCollapsed, setNavCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem(STUDIO_NAV_COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });

  const toggleNavCollapsed = () => {
    setNavCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(STUDIO_NAV_COLLAPSED_KEY, next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const registerActions = useCallback((next: FormStudioActions | null) => {
    setActions(next);
  }, []);

  const goBack = useCallback(() => {
    if (actions?.dirty && !window.confirm('Leave this section with unsaved changes?')) return;
    if (typeof window !== 'undefined' && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate('/e-forms/builder');
  }, [actions?.dirty, navigate]);

  const openSection = (next: StudioSection) => {
    if (next === section) return;
    if (actions?.dirty && !window.confirm('Leave this section with unsaved changes?')) return;
    const params = new URLSearchParams();
    params.set('section', next);
    setSearchParams(params);
    setActions(null);
  };

  if (!Number.isInteger(definitionId)) return <div className="p-6">Invalid form.</div>;
  if (definition.isLoading || !definition.data) {
    return <div className="flex min-h-[24rem] items-center justify-center">Loading Form Studio…</div>;
  }

  const published = getPublishedVersion(definition.data);
  const selected = SECTIONS.find((item) => item.id === section)!;
  const groups = Array.from(new Set(SECTIONS.map((item) => item.group)));

  return (
    <div className={cn('flex h-full min-h-0 flex-col', colors.background, colors.text)}>
      <header className={cn('sticky top-0 z-30 shrink-0 border-b', colors.border, colors.surface)}>
        <div className="flex min-h-[4.25rem] items-center gap-3 px-4 sm:px-5">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={goBack}
            className="shrink-0"
            title="Go back"
            aria-label="Go back"
          >
            <ChevronLeftIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Back</span>
          </Button>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Link to="/e-forms/builder" className="hover:underline">Form builder</Link>
              <span>/</span>
              <span>Studio</span>
              <span className="hidden sm:inline">/</span>
              <span className="hidden truncate sm:inline">{selected.label}</span>
            </div>
            <div className="mt-1 flex min-w-0 flex-wrap items-center gap-2">
              <h1 className="truncate text-lg font-semibold">{definition.data.name}</h1>
              <Badge variant={published ? 'success' : 'warning'}>
                {published ? `Published v${published.version}` : 'Draft only'}
              </Badge>
              <Badge variant={definition.data.isActive ? 'success' : 'secondary'}>
                {definition.data.isActive ? 'Enabled' : 'Disabled'}
              </Badge>
              <span className="hidden text-xs text-slate-500 md:inline">
                {actions?.statusLabel ?? (actions?.dirty ? 'Unpublished changes' : 'Draft workspace')}
              </span>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {actions?.preview ? (
              <Button variant="secondary" size="sm" onClick={actions.preview}>
                <EyeIcon className="h-4 w-4" />
                <span className="hidden sm:inline">Preview</span>
              </Button>
            ) : null}
            {actions?.save ? (
              <Button
                variant="secondary"
                size="sm"
                disabled={actions.saving}
                onClick={() => void actions.save?.()}
              >
                <SaveIcon className="h-4 w-4" />
                {actions.saving ? 'Saving…' : 'Save changes'}
              </Button>
            ) : null}
            {actions?.publish ? (
              <Button size="sm" disabled={actions.publishing} onClick={() => void actions.publish?.()}>
                <CheckCircleIcon className="h-4 w-4" />
                {actions.publishing ? 'Publishing…' : 'Publish'}
              </Button>
            ) : null}
          </div>
        </div>
      </header>

      <div className="border-b px-4 py-2 lg:hidden">
        <Select
          value={section}
          options={SECTIONS.map((item) => ({ value: item.id, label: item.label, description: item.hint }))}
          onChange={(event) => openSection(event.target.value as StudioSection)}
          showClearButton={false}
          size="sm"
        />
      </div>

      <div className="flex min-h-0 flex-1">
        <aside
          className={cn(
            'hidden shrink-0 overflow-y-auto border-r lg:block',
            navCollapsed ? 'w-14 p-2' : 'w-60 p-3',
            colors.border,
            colors.surface,
          )}
        >
          <div className={cn('mb-2 flex', navCollapsed ? 'justify-center' : 'justify-end')}>
            <button
              type="button"
              onClick={toggleNavCollapsed}
              className={cn(
                'rounded-md p-1.5 transition-colors',
                colors.textMuted,
                'hover:bg-slate-100 dark:hover:bg-slate-800',
              )}
              title={navCollapsed ? 'Expand studio menu' : 'Collapse studio menu'}
              aria-label={navCollapsed ? 'Expand studio menu' : 'Collapse studio menu'}
            >
              {navCollapsed ? (
                <ChevronRightIcon className="h-3.5 w-3.5" />
              ) : (
                <ChevronLeftIcon className="h-3.5 w-3.5" />
              )}
            </button>
          </div>
          {groups.map((group) => (
            <div key={group} className="mb-4">
              {navCollapsed ? (
                <div className="mx-auto mb-1 h-px w-6 bg-slate-200 dark:bg-slate-700" aria-hidden />
              ) : (
                <p className="mb-1 px-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                  {group}
                </p>
              )}
              <div className="space-y-0.5">
                {SECTIONS.filter((item) => item.group === group).map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => openSection(item.id)}
                    title={navCollapsed ? `${item.label} — ${item.hint}` : undefined}
                    className={cn(
                      'flex w-full rounded-lg text-left transition-colors',
                      navCollapsed
                        ? 'items-center justify-center px-0 py-2'
                        : 'items-start gap-2.5 px-2.5 py-2',
                      section === item.id
                        ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-200'
                        : 'hover:bg-slate-100 dark:hover:bg-slate-800',
                    )}
                  >
                    <span className={cn('shrink-0', !navCollapsed && 'mt-0.5')}>{item.icon}</span>
                    {navCollapsed ? null : (
                      <span className="min-w-0">
                        <span className="block text-sm font-medium">{item.label}</span>
                        <span className="block truncate text-[11px] text-slate-500">{item.hint}</span>
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </aside>

        <main className={cn('min-h-0 min-w-0 flex-1 overflow-auto', section === 'design' || section === 'reports' ? 'p-3' : 'p-4 sm:p-6')}>
          <FormStudioActionsProvider registerActions={registerActions}>
            {section === 'overview' ? <StudioOverview definitionId={definitionId} /> : null}
            {section === 'design' ? <BuilderPage studioEmbedded /> : null}
            {section === 'access' ? <PlatformSettingsPage embedded section="access" /> : null}
            {section === 'field-access' ? <PlatformSettingsPage embedded section="fields" /> : null}
            {section === 'workflow' ? <PlatformSettingsPage embedded section="workflow" /> : null}
            {section === 'relations' ? <PlatformSettingsPage embedded section="relations" /> : null}
            {section === 'reports' ? <StudioReportsPanel definitionId={definitionId} /> : null}
            {section === 'pages' ? <StudioPagesPanel definitionId={definitionId} /> : null}
            {section === 'print' ? <PlatformSettingsPage embedded section="print" /> : null}
            {section === 'publish' ? <StudioPublishPanel definitionId={definitionId} /> : null}
          </FormStudioActionsProvider>
        </main>
      </div>
    </div>
  );
};

const StudioOverview: React.FC<{ definitionId: number }> = ({ definitionId }) => {
  const definition = useEFormDefinition(definitionId);
  const reports = useEFormSavedReports(definitionId);
  const pages = useEFormAppPages(definitionId);
  const relations = useEFormRelations(definitionId);
  const print = useEFormPrintLayout(definitionId);
  const data = definition.data!;
  const draft = getDraftVersion(data);
  const published = getPublishedVersion(data);
  const schema = draft?.schemaJson;
  const fieldCount = schema
    ? flattenSchemaFields(schema).filter((field) => !isEFormDisplayField(field)).length
    : 0;
  const assignments = data.accessConfig?.assignments?.length ?? 0;
  const workflowEnabled = Boolean(schema?.settings?.requireApproval);
  const printDoc = asPrintDocument(print.data?.printLayoutJson, Boolean(data.sourceFileName));
  const hasPrint = printDoc.templates.some(
    (template) =>
      template.outputMode === 'table' ||
      template.outputMode === 'designed' ||
      template.outputMode === 'source' ||
      Boolean(template.blocks?.length),
  ) || Boolean(data.sourceFileName);
  const printDetail = hasPrint
    ? `${printDoc.templates.length} printout${printDoc.templates.length === 1 ? '' : 's'}`
    : data.sourceFileName
      ? 'Source template'
      : 'Optional';
  const checklist = [
    { label: 'Design form fields', done: fieldCount > 0, target: 'design' as StudioSection, detail: `${fieldCount} fields` },
    { label: 'Review access', done: assignments > 0 || data.accessConfig?.defaultRole !== 'none', target: 'access' as StudioSection, detail: assignments ? `${assignments} role assignments` : 'Using module access' },
    { label: 'Configure workflow', done: workflowEnabled, target: 'workflow' as StudioSection, detail: workflowEnabled ? 'Approval enabled' : 'Optional' },
    { label: 'Create a report', done: reports.data.length > 0, target: 'reports' as StudioSection, detail: `${reports.data.length} saved` },
    { label: 'Add dashboard widgets', done: pages.data.length > 0, target: 'pages' as StudioSection, detail: `${pages.data.length} widget${pages.data.length === 1 ? '' : 's'}` },
    { label: 'Configure print output', done: hasPrint || Boolean(data.sourceFileName), target: 'print' as StudioSection, detail: printDetail },
    { label: 'Publish form', done: Boolean(published), target: 'publish' as StudioSection, detail: published ? `Version ${published!.version}` : 'Not published' },
  ];
  const [params, setParams] = useSearchParams();
  const open = (section: StudioSection) => {
    const next = new URLSearchParams(params);
    next.set('section', section);
    setParams(next);
  };
  return (
    <StudioSectionIntro title="Form overview" description="See what is ready, what is optional, and what users can access." status={`${checklist.filter((item) => item.done).length}/${checklist.length} ready`}>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[
          ['Fields', fieldCount],
          ['Submissions', data._count?.submissions ?? 0],
          ['Reports', reports.data.length],
          ['Dashboard widgets', pages.data.length],
        ].map(([label, value]) => (
          <div key={String(label)} className="rounded-xl border bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
            <p className="mt-2 text-2xl font-semibold">{value}</p>
          </div>
        ))}
      </div>
      <div className="rounded-xl border bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
        <div className="border-b px-4 py-3 dark:border-slate-700">
          <h3 className="font-semibold">Setup checklist</h3>
          <p className="text-xs text-slate-500">Optional features stay out of the way until you need them.</p>
        </div>
        <div className="divide-y dark:divide-slate-700">
          {checklist.map((item) => (
            <button key={item.label} type="button" onClick={() => open(item.target)} className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800/60">
              <CheckCircleIcon className={cn('h-5 w-5 shrink-0', item.done ? 'text-emerald-500' : 'text-slate-300')} />
              <span className="min-w-0 flex-1 text-sm font-medium">{item.label}</span>
              <span className="text-xs text-slate-500">{item.detail}</span>
            </button>
          ))}
        </div>
      </div>
      {relations.data.length ? <p className="text-xs text-slate-500">{relations.data.length} cross-form relation{relations.data.length === 1 ? '' : 's'} configured.</p> : null}
    </StudioSectionIntro>
  );
};

const StudioReportsPanel: React.FC<{ definitionId: number }> = ({ definitionId }) => {
  const definition = useEFormDefinition(definitionId);
  const reports = useEFormSavedReports(definitionId);
  const [searchParams, setSearchParams] = useSearchParams();
  const reportParam = searchParams.get('report');
  const openReport = (report: 'new' | number) => {
    const next = new URLSearchParams(searchParams);
    next.set('section', 'reports');
    next.set('report', String(report));
    setSearchParams(next);
  };
  if (reportParam) {
    const savedId = reportParam === 'new' ? 'new' : Number(reportParam);
    return (
      <div className="space-y-3">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            const next = new URLSearchParams(searchParams);
            next.delete('report');
            setSearchParams(next);
          }}
        >
          Back to reports
        </Button>
        <ReportsPage
          embedded
          definitionIdOverride={definitionId}
          savedIdOverride={savedId === 'new' || Number.isInteger(savedId) ? savedId : 'new'}
        />
      </div>
    );
  }
  return (
    <StudioSectionIntro
      title="Reports"
      description="A report is a table of many submissions. Pick the fields you want as columns, save it, then open it from the menu or drop it on the central dashboard."
      status={`${reports.data.length} saved`}
    >
      <ol className="grid gap-2 sm:grid-cols-3">
        {[
          { step: '1', title: 'Choose columns', detail: 'Click form fields to add them to the table.' },
          { step: '2', title: 'Save the report', detail: 'Give it a name so you can open it again.' },
          { step: '3', title: 'Show it', detail: 'Put it in the left menu or on the central dashboard.' },
        ].map((item) => (
          <li key={item.step} className="rounded-xl border border-slate-200 px-3 py-2.5 dark:border-slate-700">
            <div className="text-xs font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-400">
              Step {item.step}
            </div>
            <div className="mt-0.5 text-sm font-medium">{item.title}</div>
            <div className="text-xs text-slate-500">{item.detail}</div>
          </li>
        ))}
      </ol>
      <div className="flex justify-end">
        <Button onClick={() => openReport('new')}>
          <PlusIcon className="h-4 w-4" />
          New table report
        </Button>
      </div>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {reports.data.map((report) => (
          <div key={report.id} className="rounded-xl border bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900">
            <TableIcon className="h-6 w-6 text-slate-400" />
            <h3 className="mt-3 font-semibold">{report.name}</h3>
            <p className="mt-1 line-clamp-2 text-sm text-slate-500">
              {report.description || definition.data?.name || 'Saved table'}
            </p>
            <div className="mt-4 flex gap-2">
              <Link to={`/e-forms/${definitionId}/reports/${report.id}`}>
                <Button size="sm" variant="secondary">Open</Button>
              </Link>
              <Button size="sm" variant="ghost" onClick={() => openReport(report.id)}>
                Edit table
              </Button>
            </div>
          </div>
        ))}
        {!reports.isLoading && !reports.data.length ? (
          <div className="rounded-xl border border-dashed p-8 text-center md:col-span-2 xl:col-span-3">
            <TableIcon className="mx-auto h-10 w-10 text-slate-300" />
            <p className="mt-3 font-medium">No reports yet</p>
            <p className="mt-1 text-sm text-slate-500">
              Start a table, click the fields you want to see, then save.
            </p>
          </div>
        ) : null}
      </div>
    </StudioSectionIntro>
  );
};

const StudioPagesPanel: React.FC<{ definitionId: number }> = ({ definitionId }) => {
  const pages = useEFormAppPages(definitionId);
  const [searchParams, setSearchParams] = useSearchParams();
  const { showSuccess, showError } = useNotification();
  const confirm = useConfirm();
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const pageParam = searchParams.get('page');

  const removePage = async (pageId: number, pageName: string) => {
    const ok = await confirm({
      title: 'Delete dashboard widget?',
      message: `“${pageName}” will be removed from the central dashboard and menu.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmVariant: 'danger',
    });
    if (!ok) return;
    setDeletingId(pageId);
    try {
      await deleteEFormAppPage(pageId);
      await pages.mutate();
      showSuccess('Dashboard widget deleted');
      if (searchParams.get('page') === String(pageId)) {
        const next = new URLSearchParams(searchParams);
        next.delete('page');
        setSearchParams(next);
      }
    } catch (error: any) {
      showError(error?.response?.data?.message || error?.message || 'Could not delete dashboard widget');
    } finally {
      setDeletingId(null);
    }
  };

  if (pageParam) {
    const pageId = pageParam === 'new' ? 'new' : Number(pageParam);
    return (
      <div className="space-y-3">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            const next = new URLSearchParams(searchParams);
            next.delete('page');
            setSearchParams(next);
          }}
        >
          Back to widgets
        </Button>
        <EFormAppPageEditor
          embedded
          definitionIdOverride={definitionId}
          pageIdOverride={pageId === 'new' || Number.isInteger(pageId) ? pageId : 'new'}
          onDeleted={() => {
            const next = new URLSearchParams(searchParams);
            next.delete('page');
            setSearchParams(next);
            void pages.mutate();
          }}
        />
      </div>
    );
  }
  return (
    <StudioSectionIntro
      title="Dashboard widgets"
      description="Configure tiles for this form, then add them to the central dashboard in Dashboard settings. You do not create a separate dashboard per form."
      status={`${pages.data.length} widget${pages.data.length === 1 ? '' : 's'}`}
    >
      <div className="flex justify-end">
        <Link to={`/e-forms/builder/${definitionId}?section=pages&page=new`}>
          <Button><PlusIcon className="h-4 w-4" />New widget</Button>
        </Link>
      </div>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {pages.data.map((page) => (
          <div
            key={page.id}
            className="relative rounded-xl border bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900"
          >
            <button
              type="button"
              className="absolute right-2 top-2 rounded-lg p-1.5 text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50 dark:hover:bg-red-950/40"
              disabled={deletingId === page.id}
              onClick={() => void removePage(page.id, page.name)}
              title="Delete widget"
              aria-label={`Delete ${page.name}`}
            >
              <TrashIcon className="h-3.5 w-3.5" />
            </button>
            <ViewColumnsIcon className="h-6 w-6 text-slate-400" />
            <h3 className="mt-3 pr-8 font-semibold">{page.name}</h3>
            <p className="mt-1 line-clamp-2 text-sm text-slate-500">{page.description || 'Dashboard tile for this form'}</p>
            <div className="mt-4 flex gap-2">
              <Link to={`/e-forms/apps/${page.id}`}><Button size="sm" variant="secondary">Open</Button></Link>
              <Link to={`/e-forms/builder/${definitionId}?section=pages&page=${page.id}`}><Button size="sm" variant="ghost">Edit widget</Button></Link>
            </div>
          </div>
        ))}
        {!pages.isLoading && !pages.data.length ? (
          <div className="rounded-xl border border-dashed p-8 text-center md:col-span-2 xl:col-span-3">
            <ViewColumnsIcon className="mx-auto h-10 w-10 text-slate-300" />
            <p className="mt-3 font-medium">No dashboard widgets yet</p>
            <p className="mt-1 text-sm text-slate-500">
              Add a widget for this form, then place it on the central dashboard.
            </p>
          </div>
        ) : null}
      </div>
    </StudioSectionIntro>
  );
};

const StudioPublishPanel: React.FC<{ definitionId: number }> = ({ definitionId }) => {
  const definition = useEFormDefinition(definitionId);
  const { showSuccess, showError } = useNotification();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const draft = definition.data ? getDraftVersion(definition.data) : undefined;
  const published = definition.data ? getPublishedVersion(definition.data) : undefined;
  const issues = useMemo(
    () => (draft?.schemaJson ? validateSchemaForPublish(draft.schemaJson) : []),
    [draft?.schemaJson],
  );
  const publish = async () => {
    if (issues.length) return showError('Resolve the validation issues before publishing.');
    setBusy(true);
    try {
      await publishEFormDefinition(definitionId);
      await definition.mutate();
      showSuccess('Form published');
    } catch (error: any) {
      showError(error?.response?.data?.message || error?.message || 'Publish failed');
    } finally {
      setBusy(false);
    }
  };
  const unpublish = async () => {
    if (!window.confirm('Unpublish this form? Fillers will no longer see it.')) return;
    setBusy(true);
    try {
      await unpublishEFormDefinition(definitionId);
      await definition.mutate();
      showSuccess('Form unpublished');
    } finally {
      setBusy(false);
    }
  };
  return (
    <StudioSectionIntro title="Review and publish" description="Validate the draft and make it available to fillers when it is ready." status={published ? `Live v${published.version}` : 'Not live'}>
      <div className="grid gap-4 lg:grid-cols-[1fr_20rem]">
        <div className="rounded-xl border bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-900">
          <h3 className="font-semibold">Pre-publish validation</h3>
          {issues.length ? (
            <div className="mt-4 space-y-2">
              {issues.map((issue, index) => (
                <button key={`${issue.fieldKey ?? 'form'}-${index}`} type="button" onClick={() => navigate(`/e-forms/builder/${definitionId}?section=design`)} className="flex w-full gap-2 rounded-lg bg-amber-50 p-3 text-left text-sm text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
                  <span>•</span><span>{issue.message}</span>
                </button>
              ))}
            </div>
          ) : (
            <div className="mt-4 flex items-center gap-2 rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-200">
              <CheckCircleIcon className="h-5 w-5" />Draft is ready to publish.
            </div>
          )}
        </div>
        <div className="rounded-xl border bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-900">
          <LockIcon className="h-7 w-7 text-slate-400" />
          <h3 className="mt-3 font-semibold">{published ? 'Publish a new version' : 'Publish this form'}</h3>
          <p className="mt-1 text-sm text-slate-500">Publishing freezes this draft for new submissions and creates the next editable draft.</p>
          <Button className="mt-4 w-full" disabled={busy || issues.length > 0} onClick={() => void publish()}>
            {busy ? 'Working…' : 'Publish now'}
          </Button>
          {published ? <Button className="mt-2 w-full" variant="secondary" disabled={busy} onClick={() => void unpublish()}>Unpublish</Button> : null}
        </div>
      </div>
    </StudioSectionIntro>
  );
};

export default FormStudioPage;
